import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { authenticatedFetch } from "@/libs/api";
import {
  Card,
  Form,
  Input,
  Select,
  Button,
  Upload,
  Typography,
  Row,
  Col,
  Space,
  message,
  theme,
} from "antd";
import {
  BugOutlined,
  UploadOutlined,
  ArrowLeftOutlined,
  InboxOutlined,
} from "@ant-design/icons";
import PageLayout from "@components/pageLayout/PageLayout.react";

const { Title } = Typography;
const { TextArea } = Input;
const { Dragger } = Upload;

const CATEGORY_OPTIONS = [
  { value: "ui", label: "UI / Frontend" },
  { value: "backend", label: "Backend / API" },
  { value: "data", label: "Data Issue" },
  { value: "engine", label: "Engine" },
  { value: "other", label: "Other" },
];

export default function BugReportForm() {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const [fileList, setFileList] = useState([]);

  const handleSubmit = async (values) => {
    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append("title", values.title);
      formData.append("description", values.description);
      formData.append("priority", values.priority || "medium");
      if (values.category) formData.append("category", values.category);
      if (values.page_url) formData.append("page_url", values.page_url);

      fileList.forEach((file) => {
        formData.append("files", file.originFileObj || file);
      });

      const res = await authenticatedFetch("/api/bug-reports", {
        method: "POST",
        body: formData,
      });
      const result = await res.json();

      if (result.success) {
        message.success("Bug report created successfully");
        navigate(`/bug-reports/${result.data.id}`);
      } else {
        message.error(result.message || "Failed to create bug report");
      }
    } catch (err) {
      message.error("Failed to create bug report");
    } finally {
      setSubmitting(false);
    }
  };

  const uploadProps = {
    multiple: true,
    fileList,
    beforeUpload: () => false, // prevent auto upload
    onChange: ({ fileList: newFileList }) => setFileList(newFileList),
    accept: ".jpg,.jpeg,.png,.gif,.webp,.bmp,.svg,.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.log,.json,.zip,.gz",
  };

  return (
    <PageLayout active="bugReports">
      <div style={{ maxWidth: 800, margin: "0 auto" }}>
        <Row align="middle" style={{ marginBottom: 24 }}>
          <Col>
            <Button
              type="text"
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate("/bug-reports")}
              style={{ marginRight: 8 }}
            />
          </Col>
          <Col>
            <Title level={3} style={{ margin: 0 }}>
              <BugOutlined style={{ marginRight: 8 }} />
              Report a Bug
            </Title>
          </Col>
        </Row>

        <Card>
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            initialValues={{ priority: "medium" }}
          >
            <Form.Item
              name="title"
              label="Title"
              rules={[{ required: true, message: "Please enter a title" }]}
            >
              <Input placeholder="Brief description of the bug" maxLength={256} />
            </Form.Item>

            <Form.Item
              name="description"
              label="Description"
              rules={[{ required: true, message: "Please describe the bug" }]}
            >
              <TextArea
                rows={6}
                placeholder="Steps to reproduce, expected vs actual behavior, etc."
                maxLength={5000}
                showCount
              />
            </Form.Item>

            <Row gutter={16}>
              <Col xs={24} sm={8}>
                <Form.Item name="priority" label="Priority">
                  <Select
                    options={[
                      { value: "low", label: "Low" },
                      { value: "medium", label: "Medium" },
                      { value: "high", label: "High" },
                      { value: "critical", label: "Critical" },
                    ]}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={8}>
                <Form.Item name="category" label="Category">
                  <Select
                    placeholder="Select category"
                    allowClear
                    options={CATEGORY_OPTIONS}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={8}>
                <Form.Item name="page_url" label="Page URL">
                  <Input placeholder="e.g. /systems/ABC123" />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item label="Attachments (screenshots, logs, etc.)">
              <Dragger {...uploadProps}>
                <p className="ant-upload-drag-icon">
                  <InboxOutlined />
                </p>
                <p className="ant-upload-text">Click or drag files to upload</p>
                <p className="ant-upload-hint">
                  Max 10 files, 10MB each. Images, PDFs, documents, logs, etc.
                </p>
              </Dragger>
            </Form.Item>

            <Form.Item style={{ marginBottom: 0, textAlign: "right" }}>
              <Space>
                <Button onClick={() => navigate("/bug-reports")}>Cancel</Button>
                <Button type="primary" htmlType="submit" loading={submitting}>
                  Submit Bug Report
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Card>
      </div>
    </PageLayout>
  );
}
